import React, { CSSProperties, useMemo, useState } from 'react';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useHistory } from 'react-router-dom';
import ModalDialog from 'components/modal-dialog/ModalDialog';
import CommunityInfoForm from 'components/community-info-form/CommunityInfoForm';
import swal from 'sweetalert2';
import DeleteIcon from '!!react-svg-loader!../space-card/icons/icon--trash.svg';
import CreateIcon from '!!react-svg-loader!../space-card/icons/icon--space-edit.svg';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { useCommunityDetails } from 'atoms/community-details';
import { match } from 'ts-pattern';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ReportIcon from '!!react-svg-loader!../space-card/icons/icon--report.svg';
import ArchiveIcon from '!!react-svg-loader!../space-card/icons/icon--archive.svg';
import ROUTES from 'constants/routes';
import { useModal } from 'mui-modal-provider';
import CorridorReportModal from 'components/corridor-report-modal/corridor-report-modal';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const iconStyle = {
  width: '22px',
  color: '#34214c',
};

export default function CommunitySettingsTrigger(p: {
  communityId: string;
  style?: CSSProperties;
  className?: string;
}) {
  const { _ } = useLingui();
  const history = useHistory();
  const { showModal } = useModal();
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const communityHandle = useCommunityDetails({
    communityId: p.communityId,
    autoLoad: false,
  });
  const { community } = communityHandle;

  const isOwner = useMemo(() => {
    return (community?.roles?.indexOf('OWNER') ?? -1) >= 0;
  }, [community]);
  const isArchived = !!community?.deactivatedAt;

  if (!community || community.roles.length === 0) return null;

  return (
    <>
      <PopupState variant='popover' popupId='community-settings-menu'>
        {(popupState) => (
          <>
            <IconButton
              style={p.style}
              {...bindTrigger(popupState)}
              className={p.className}
            >
              <MoreVertIcon style={{ height: '20' }} />
            </IconButton>
            <Menu {...bindMenu(popupState)}>
              {match({ isOwner, isArchived })
                .with({ isOwner: true, isArchived: true }, () => [
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      communityHandle.restoreArchivedCommunity();
                    }}
                    key='restore-corridor'
                  >
                    <ListItemIcon>
                      <LockOpenIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Restore Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      deleteCommunity();
                    }}
                    key='delete-corridor'
                  >
                    <ListItemIcon>
                      <DeleteIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Delete Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                ])
                .with({ isOwner: true }, () => [
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      setEditDialogVisible(true);
                    }}
                    key='edit-corridor'
                  >
                    <ListItemIcon>
                      <CreateIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Edit Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      leaveCommunity();
                    }}
                    key='leave-corridor'
                  >
                    <ListItemIcon>
                      <ExitToAppIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Leave Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      archiveCommunity();
                    }}
                    key='archive-corridor'
                  >
                    <ListItemIcon>
                      <ArchiveIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Archive Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      deleteCommunity();
                    }}
                    key='delete-corridor'
                  >
                    <ListItemIcon>
                      <DeleteIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Delete Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                ])
                .with({ isArchived: true }, () => [
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      leaveCommunity();
                    }}
                    key='leave-corridor'
                  >
                    <ListItemIcon>
                      <ExitToAppIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Leave Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                ])
                .otherwise(() => [
                  <MenuItem
                    onClick={() => {
                      const modal = showModal(CorridorReportModal, {
                        onClose: () => {
                          modal.destroy();
                        },
                      });
                    }}
                    key='report-corridor'
                  >
                    <ListItemIcon>
                      <ReportIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Report Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      leaveCommunity();
                    }}
                    key='leave-corridor'
                  >
                    <ListItemIcon>
                      <ExitToAppIcon style={iconStyle} />
                    </ListItemIcon>
                    <ListItemText>
                      <Trans>Leave Community</Trans>
                    </ListItemText>
                  </MenuItem>,
                ])}
            </Menu>
          </>
        )}
      </PopupState>
      {editDialogVisible && (
        <ModalDialog
          isCloseable
          title={_(msg`Edit Community`)}
          onClose={() => {
            setEditDialogVisible(false);
          }}
        >
          <CommunityInfoForm
            community={community}
            additionalTargetNames={[]}
            onSuccess={() => {
              setEditDialogVisible(false);
            }}
          />
        </ModalDialog>
      )}
    </>
  );

  async function deleteCommunity() {
    const { isConfirmed } = await swal.fire({
      title: _(msg`Are you sure you want to delete this community?`),
      showCancelButton: true,
      // eslint-disable-next-line lingui/no-unlocalized-strings
      footer: `<div>
        <strong>${_(msg`This operation can not be undone.`)}</strong> ${_(
        msg`Unless you are absolutely sure, we recommend archiving this community.`,
      )}
      </div>`,
      confirmButtonText: _(msg`Yes`),
      cancelButtonText: _(msg`Cancel`),
    });
    if (!isConfirmed) return;
    await communityHandle.deleteCommunity();
    history.push(ROUTES.LANDING);
  }

  async function archiveCommunity() {
    const { isConfirmed } = await swal.fire({
      title: _(msg`Are you sure you want to archive this community?`),
      showCancelButton: true,
      footer: `<div>${_(msg`You will be able to restore it later.`)}</div>`,
      confirmButtonText: _(msg`Yes`),
      cancelButtonText: _(msg`Cancel`),
    });
    if (!isConfirmed) return;
    await communityHandle.archiveCommunity();
  }

  async function reportCommunity() {
    const { isConfirmed } = await swal.fire({
      title: _(msg`Are you sure you want to report this community?`),
      showCancelButton: true,
      footer: `<div>${_(
        msg`Our team will review this and take action promptly.`,
      )}</div>`,
      confirmButtonText: _(msg`Yes`),
      cancelButtonText: _(msg`Cancel`),
    });
    if (!isConfirmed) return;
    if (await communityHandle.reportCommunity()) {
      history.push(ROUTES.LANDING);
    }
  }

  async function leaveCommunity() {
    const owners =
      community?.members?.filter((it) => it.roles.indexOf('OWNER') >= 0) ?? [];
    if (isOwner && owners.length === 1) {
      await swal.fire({
        title: _(msg`You are the only admin`),
        html: `${_(
          msg`Leaving this community will make it inaccessible. Please add another admin or delete the community.`,
        )}`,
        confirmButtonText: _(msg`OK`),
        cancelButtonText: _(msg`Cancel`),
      });
      return;
    }
    const { isConfirmed } = await swal.fire({
      title: _(msg`Are you sure you want to leave this community?`),
      showCancelButton: true,
      confirmButtonText: _(msg`Yes`),
      cancelButtonText: _(msg`Cancel`),
    });
    if (!isConfirmed) return;
    if (await communityHandle.leaveCommunity()) {
      history.push(ROUTES.LANDING);
    }
  }
}
